import axios from 'axios';
import { hasSmartHealingOriginSession, isSmartHealingLinkedUser } from '../utils/smartHealingAccess';
import { consumeAppNext, clearAuthStorage, readAuthStorage, writeAppNext, writeAuthStorage } from './authStorage';
import { deriveAdminFlag, getOrCreateDeviceId, isAppEnvironment, isJwtExpired, parseJwt, persistAdminFlag } from './authShared';

const initialState = {
  status: 'booting',
  adminStatus: 'unknown',
  accessToken: null,
  refreshToken: null,
  userNo: null,
  isAdmin: false,
  intent: null,
  loginModal: null,
  source: 'web',
  lastVerifiedAt: null,
  lastError: null,
  smartHealing: {
    hasOriginSession: false,
    isLinkedUser: false,
  },
};

let snapshot = initialState;
let didBootstrap = false;
let verifyPromise = null;
let refreshPromise = null;
const listeners = new Set();

const emitChange = () => {
  listeners.forEach((listener) => listener());
};

const setSnapshot = (updater) => {
  const nextSnapshot = typeof updater === 'function' ? updater(snapshot) : updater;
  snapshot = nextSnapshot;
  emitChange();
  return snapshot;
};

const buildSmartHealingState = (userNo) => {
  return {
    hasOriginSession: hasSmartHealingOriginSession(),
    isLinkedUser: userNo ? isSmartHealingLinkedUser(userNo) : false,
  };
};

const buildAuthenticatedState = (storedAuth, currentState = snapshot, overrides = {}) => {
  const isAuthenticated = Boolean(storedAuth.accessToken);
  const isAdmin = Boolean(storedAuth.isAdmin);

  return {
    ...currentState,
    status: isAuthenticated ? 'authenticated' : 'unauthenticated',
    adminStatus: !isAuthenticated ? 'denied' : (isAdmin ? 'granted' : 'unknown'),
    accessToken: storedAuth.accessToken,
    refreshToken: storedAuth.refreshToken,
    userNo: storedAuth.userNo,
    isAdmin,
    loginModal: null,
    source: overrides.source || currentState.source || (isAppEnvironment() ? 'app' : 'web'),
    smartHealing: buildSmartHealingState(storedAuth.userNo),
    ...overrides,
  };
};

const setUnauthenticatedState = (currentState = snapshot, overrides = {}) => {
  return setSnapshot({
    ...currentState,
    status: 'unauthenticated',
    adminStatus: 'denied',
    accessToken: null,
    refreshToken: null,
    userNo: null,
    isAdmin: false,
    intent: overrides.preserveIntent ? currentState.intent : null,
    loginModal: null,
    smartHealing: buildSmartHealingState(null),
    lastError: overrides.lastError || null,
    ...overrides,
  });
};

const applyStoredAuth = (overrides = {}) => {
  const storedAuth = readAuthStorage();
  return setSnapshot(buildAuthenticatedState(storedAuth, snapshot, overrides));
};

const resolveGatewayUrl = (path) => {
  const baseUrl = process.env.REACT_APP_API_URL
    ? String(process.env.REACT_APP_API_URL).replace(/\/$/, '')
    : '';

  return baseUrl ? `${baseUrl}${path}` : path;
};

export const authStore = {
  getSnapshot() {
    return snapshot;
  },

  subscribe(listener) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  bootstrap() {
    if (didBootstrap && snapshot.status !== 'booting') {
      return snapshot;
    }

    didBootstrap = true;
    const storedAuth = readAuthStorage();
    const source = isAppEnvironment() ? 'app' : 'web';

    if (!storedAuth.accessToken) {
      return setUnauthenticatedState(snapshot, {
        source,
        lastError: null,
      });
    }

    return setSnapshot({
      ...snapshot,
      status: 'booting',
      adminStatus: storedAuth.isAdmin ? 'checking' : 'unknown',
      accessToken: storedAuth.accessToken,
      refreshToken: storedAuth.refreshToken,
      userNo: storedAuth.userNo,
      isAdmin: storedAuth.isAdmin,
      loginModal: null,
      lastError: null,
      source,
      smartHealing: buildSmartHealingState(storedAuth.userNo),
    });
  },

  hydrateFromStorage(reason = 'hydrate') {
    return applyStoredAuth({
      lastError: reason === 'storage' ? null : snapshot.lastError,
    });
  },

  rememberIntent(intent) {
    if (!intent || !intent.kind) {
      return snapshot;
    }

    if (intent.kind === 'app' && intent.path) {
      writeAppNext(intent.path);
    }

    return setSnapshot({
      ...snapshot,
      intent,
    });
  },

  openLoginModal({ message = '', intent = null } = {}) {
    if (snapshot.status === 'authenticated') {
      return snapshot;
    }

    let nextIntent = snapshot.intent;
    if (intent?.kind) {
      if (intent.kind === 'app' && intent.path) {
        writeAppNext(intent.path);
      }
      nextIntent = intent;
    }

    return setSnapshot({
      ...snapshot,
      intent: nextIntent,
      loginModal: {
        kind: 'login',
        message: String(message || '').trim(),
        requestedAt: Date.now(),
      },
    });
  },

  closeLoginModal() {
    if (!snapshot.loginModal) {
      return snapshot;
    }

    return setSnapshot({
      ...snapshot,
      loginModal: null,
    });
  },

  consumeIntent() {
    const currentIntent = snapshot.intent;

    if (currentIntent) {
      consumeAppNext();

      setSnapshot({
        ...snapshot,
        intent: null,
      });

      return currentIntent;
    }

    const appNext = consumeAppNext();
    if (!appNext) {
      return null;
    }

    return {
      kind: 'app',
      path: appNext,
      reason: 'session-storage-fallback',
      createdAt: Date.now(),
    };
  },

  loginSuccess(payload = {}, options = {}) {
    const storedAuth = writeAuthStorage(payload);
    const nextIntent = options.preserveExistingIntent ? snapshot.intent : null;

    return setSnapshot(buildAuthenticatedState(storedAuth, snapshot, {
      adminStatus: storedAuth.isAdmin ? 'granted' : 'unknown',
      intent: nextIntent,
      source: options.source || snapshot.source || (isAppEnvironment() ? 'app' : 'web'),
      lastError: null,
    }));
  },

  logout({ reason = 'user', clearAppNext = true, preserveIntent = false } = {}) {
    clearAuthStorage({ clearAppNext });
    return setUnauthenticatedState(snapshot, {
      source: isAppEnvironment() ? 'app' : 'web',
      lastError: reason === 'user' ? null : reason,
      preserveIntent,
    });
  },

  async verifySession() {
    if (verifyPromise) {
      return verifyPromise;
    }

    const run = async () => {
      const startedAuthenticated = snapshot.status === 'authenticated';
      let currentAuth = readAuthStorage();
      if (!currentAuth.accessToken) {
        setUnauthenticatedState(snapshot);
        return false;
      }

      if (isJwtExpired(currentAuth.accessToken, 15)) {
        const refreshed = await this.refreshSession();
        if (!refreshed) {
          return false;
        }
        currentAuth = readAuthStorage();
        if (!currentAuth.accessToken) {
          setUnauthenticatedState(snapshot);
          return false;
        }
      }

      setSnapshot({
        ...snapshot,
        status: startedAuthenticated ? 'authenticated' : 'booting',
        accessToken: currentAuth.accessToken,
        refreshToken: currentAuth.refreshToken,
        userNo: currentAuth.userNo,
        isAdmin: currentAuth.isAdmin,
        adminStatus: currentAuth.isAdmin ? 'granted' : 'checking',
        smartHealing: buildSmartHealingState(currentAuth.userNo),
      });

      try {
        const { data } = await axios.get(resolveGatewayUrl('/api/v1/auth/me'), {
          headers: {
            Authorization: `Bearer ${currentAuth.accessToken}`,
          },
        });

        if (readAuthStorage().accessToken !== currentAuth.accessToken) return false;
        const tokenPayload = parseJwt(currentAuth.accessToken) || {};
        const adminFlag = deriveAdminFlag({
          ...tokenPayload,
          ...(data || {}),
        });

        const storedAuth = writeAuthStorage({
          access_token: currentAuth.accessToken,
          refresh_token: currentAuth.refreshToken,
          user_no: data?.user_no ?? currentAuth.userNo,
          ...(data || {}),
        });

        setSnapshot(buildAuthenticatedState(storedAuth, snapshot, {
          isAdmin: adminFlag,
          adminStatus: adminFlag ? 'granted' : 'denied',
          lastVerifiedAt: Date.now(),
          lastError: null,
          smartHealing: buildSmartHealingState(storedAuth.userNo),
        }));

        return true;
      } catch (error) {
        if (readAuthStorage().accessToken !== currentAuth.accessToken) return false;
        const status = error?.response?.status;
        if (status === 401) {
          this.logout({ reason: 'verify-unauthorized' });
          return false;
        }

        const fallbackAuth = readAuthStorage();
        persistAdminFlag(false);
        setSnapshot(buildAuthenticatedState(fallbackAuth, snapshot, {
          isAdmin: false,
          adminStatus: 'unknown',
          lastError: error,
          smartHealing: buildSmartHealingState(fallbackAuth.userNo),
        }));

        return false;
      }
    };

    verifyPromise = run().finally(() => {
      verifyPromise = null;
    });

    return verifyPromise;
  },

  async ensureAdmin() {
    const currentState = snapshot;
    if (currentState.status !== 'authenticated') {
      return false;
    }

    if (currentState.adminStatus === 'granted') {
      return true;
    }

    setSnapshot({
      ...currentState,
      adminStatus: 'checking',
    });

    const verified = await this.verifySession();
    if (!verified) {
      return false;
    }

    const nextState = snapshot;
    if (nextState.status !== 'authenticated') {
      return false;
    }

    if (nextState.isAdmin) {
      if (nextState.adminStatus !== 'granted') {
        setSnapshot({
          ...nextState,
          adminStatus: 'granted',
        });
      }
      return true;
    }

    if (nextState.adminStatus !== 'denied') {
      setSnapshot({
        ...nextState,
        adminStatus: 'denied',
      });
    }

    return false;
  },

  async refreshSession() {
    if (refreshPromise) {
      return refreshPromise;
    }

    const run = async () => {
      const currentAuth = readAuthStorage();
      if (!currentAuth.refreshToken) {
        this.logout({ reason: 'refresh-missing' });
        return false;
      }

      try {
        const apiUrl = process.env.REACT_APP_API_URL
          ? String(process.env.REACT_APP_API_URL).replace(/\/$/, '')
          : '';

        const deviceId = getOrCreateDeviceId();
        const response = await fetch(`${apiUrl}/api/v1/auth/refresh`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Device-Id': deviceId,
          },
          body: JSON.stringify({
            refresh_token: currentAuth.refreshToken,
            device_id: deviceId,
          }),
        });

        if (readAuthStorage().refreshToken !== currentAuth.refreshToken) return false;
        if (!response.ok) {
          if (response.status === 401) {
            this.logout({ reason: 'refresh-rejected' });
          } else {
            persistAdminFlag(false);
            setSnapshot(buildAuthenticatedState(readAuthStorage(), snapshot, {
              isAdmin: false, adminStatus: 'unknown', lastError: 'refresh-unavailable',
            }));
          }
          return false;
        }

        const data = await response.json();
        if (readAuthStorage().refreshToken !== currentAuth.refreshToken) return false;
        if (typeof data?.access_token !== 'string' || !data.access_token.trim()
          || typeof data?.refresh_token !== 'string' || !data.refresh_token.trim()) {
          persistAdminFlag(false);
          setSnapshot(buildAuthenticatedState(readAuthStorage(), snapshot, {
            isAdmin: false, adminStatus: 'unknown', lastError: 'refresh-unavailable',
          }));
          return false;
        }

        this.loginSuccess({ ...data, user_no: data.user_no ?? currentAuth.userNo }, {
          source: snapshot.source || (isAppEnvironment() ? 'app' : 'web'),
          preserveExistingIntent: true,
        });
        // Token rotation is not a new verified identity/admin lookup.
        persistAdminFlag(false);
        setSnapshot({ ...snapshot, isAdmin: false, adminStatus: 'unknown', lastVerifiedAt: null });

        return true;
      } catch (error) {
        if (readAuthStorage().refreshToken !== currentAuth.refreshToken) return false;
        persistAdminFlag(false);
        setSnapshot(buildAuthenticatedState(readAuthStorage(), snapshot, {
          isAdmin: false, adminStatus: 'unknown', lastError: 'refresh-unavailable',
        }));
        return false;
      }
    };

    refreshPromise = run().finally(() => {
      refreshPromise = null;
    });

    return refreshPromise;
  },
};
